/* =============================================================================
 * Admin API client — docs/openapi/admin-api.yaml (cmd/admin, :8081)
 * Every call except login sends `Authorization: Bearer <jwt>` (4-hour token).
 * ========================================================================== */

import { ADMIN_API_BASE } from '../config.ts';
import { ApiError, createClient } from './http.ts';
import type {
  AdminCreateRequest,
  AdminUpdateRequest,
  Candidate,
  CandidateDetails,
  CandidateType,
  ExportRange,
  LegalDocument,
  RegionCounts,
} from '../types.ts';

/* ------------------------------ session ---------------------------------- */

const TOKEN_KEY = 'shivavakta.admin.token';
let memoryToken: string | null = null;
const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** JWT `exp` claim in ms, or null if unreadable. */
function tokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const session = {
  get token(): string | null {
    const t = memoryToken ?? readStored();
    if (t) {
      const exp = tokenExpiry(t);
      if (exp !== null && exp < Date.now()) {
        session.clear();
        return null;
      }
    }
    return t;
  },
  set(token: string) {
    memoryToken = token;
    try {
      // sessionStorage (not localStorage): closing the tab ends the session.
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch { /* storage blocked — memory only */ }
    listeners.forEach((l) => l());
  },
  clear() {
    memoryToken = null;
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
    listeners.forEach((l) => l());
  },
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

const client = createClient({
  service: 'admin',
  baseUrl: ADMIN_API_BASE,
  getToken: () => session.token,
  onUnauthorized: () => session.clear(),
});

/* ------------------------------- auth ------------------------------------ */

export async function login(email: string, password: string): Promise<void> {
  const res = await client.post<{ token: string }>('/admin/login', { email, password });
  session.set(res.token);
}

export function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'ईमेल किंवा पासवर्ड चुकीचा आहे.';
    if (err.status === 429) {
      const min = err.retryAfterSec ? Math.max(1, Math.ceil(err.retryAfterSec / 60)) : null;
      return min
        ? `खूप लॉगिन प्रयत्न झाले. ${min} मिनिटांनी पुन्हा प्रयत्न करा.`
        : 'खूप लॉगिन प्रयत्न झाले. थोड्या वेळाने पुन्हा प्रयत्न करा.';
    }
    return err.message;
  }
  return 'लॉगिन करताना अनपेक्षित त्रुटी आली.';
}

/* ----------------------------- candidates -------------------------------- */

/** The backend returns every candidate in one array (no server-side paging). */
export async function listCandidates(): Promise<Candidate[]> {
  const res = await client.get<Candidate[] | null>('/admin/candidates');
  return Array.isArray(res) ? res : [];
}

/** Category details for one candidate — a separate call per the spec. */
export async function getCandidateDetails(
  id: string,
  candidate_type: CandidateType,
): Promise<CandidateDetails> {
  const res = await client.get<CandidateDetails & { candidate_id?: string }>('/admin/candidates', {
    query: { id, candidate_type },
  });
  // candidate_id is read-only; strip it so the object can be sent back in a PUT.
  const { candidate_id: _omit, ...details } = res;
  return details as CandidateDetails;
}

export function createCandidate(body: AdminCreateRequest): Promise<{ id: string }> {
  return client.post<{ id: string }>('/admin/candidates', body);
}

export function updateCandidate(id: string, body: AdminUpdateRequest): Promise<{ msg: string }> {
  return client.put<{ msg: string }>(`/admin/candidates/${encodeURIComponent(id)}`, body);
}

export function deleteCandidate(id: string): Promise<{ msg: string }> {
  return client.del<{ msg: string }>(`/admin/candidates/${encodeURIComponent(id)}`);
}

export async function getRegionCounts(): Promise<RegionCounts> {
  return (await client.get<RegionCounts | null>('/admin/candidates/region-count')) ?? {};
}

/** Server-built .xlsx. daily = last 24 h, weekly = last 7 days, total = everyone. */
export function exportCandidates(range: ExportRange) {
  return client.download('/admin/candidates/export', { query: { range } });
}

export function candidateErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'अनपेक्षित त्रुटी आली.';
  const s = (err.serverMessage ?? '').toLowerCase();
  if (err.status === 409) return 'सर्व्हरवर तात्पुरती अडचण आली. कृपया पुन्हा प्रयत्न करा.';
  if (err.status === 404) return 'हा स्पर्धक सापडला नाही — कदाचित तो आधीच हटवला गेला असेल.';
  if (s.includes('date of birth') || s.includes('district') || s.includes('gender') || s.includes('language')) {
    return 'जन्मतारीख, लिंग, भाषा, जिल्हा किंवा शैक्षणिक तपशील अवैध आहे.';
  }
  if (s.includes('details') || s.includes('category')) return 'गटाचा तपशील अपूर्ण किंवा अवैध आहे.';
  return err.serverMessage ? `${err.message} (${err.serverMessage})` : err.message;
}

/* ----------------------------- legal document ---------------------------- */

/** Current version, or null if nothing has ever been published (404). */
export async function getCurrentLegalDoc(): Promise<LegalDocument | null> {
  try {
    return await client.get<LegalDocument>('/admin/legal-docs');
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Every version ever published, oldest first. */
export async function getLegalDocVersions(): Promise<LegalDocument[]> {
  const res = await client.get<LegalDocument[] | null>('/admin/legal-docs', {
    query: { docs: 'all' },
  });
  return Array.isArray(res) ? res : [];
}

/** Publishes a new version (becomes current). There is no update-in-place or delete. */
export function publishLegalDoc(content: string): Promise<{ id: string; version: number }> {
  return client.post<{ id: string; version: number }>('/admin/legal-docs', { content });
}
